'use client';

import { useEffect, useRef, useState } from 'react';
import Media from 'components/shared/Media/Media';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { DisplayHeadline } from 'components/fall-2026/modules/RichTextHeadline/DisplayHeadline';
import Link from 'components/shared/Link/Link';
import { isValidCta } from 'components/shared/CTA/CTA';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { usePrefersReducedMotion } from 'hooks/usePrefersReducedMotion';
import HeroCTA from 'components/HeroConsolidated/shared/HeroCTA';
import type { HeroConsolidatedProps } from 'components/HeroConsolidated/types';

const CTA_ANIMATION = 'animate-hero-fade-in-cta motion-reduce:animate-none';

export function HeroVideoRevealVariant(props: HeroConsolidatedProps) {
  const {
    sys,
    sectionName,
    headline,
    copy,
    eyebrowText,
    desktopMedia,
    mobileMedia,
    video,
    headingLevel = 'h1',
  } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoShellRef = useRef<HTMLDivElement>(null);
  const pausedByVisitorRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  const videoUrl = video?.video?.url;

  useEffect(() => {
    if (prefersReducedMotion) videoRef.current?.pause();
    else if (!pausedByVisitorRef.current) void videoRef.current?.play().catch(() => setIsPlaying(false));
  }, [videoUrl, prefersReducedMotion]);

  useEffect(() => {
    const shell = videoShellRef.current;
    if (!shell || prefersReducedMotion) return;
    let frame = 0;
    const updateScale = () => {
      const bounds = shell.parentElement?.getBoundingClientRect();
      if (!bounds) return;
      const progress = (window.innerHeight - bounds.top) / (window.innerHeight + bounds.height);
      // The video grows from 0.94 to 1 while the shell is 27% to 42% of the way through the viewport.
      const growth = Math.min(1, Math.max(0, (progress - 0.27) / 0.15));
      // Written straight to the element so scrolling doesn't re-render React.
      shell.style.setProperty('--video-scale', String(0.94 + growth * 0.06));
    };
    const handleScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        updateScale();
      });
    };
    updateScale();
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [prefersReducedMotion]);

  function handleVideoToggle() {
    const player = videoRef.current;
    if (!player) return;
    pausedByVisitorRef.current = !player.paused;
    if (player.paused) void player.play().catch(() => setIsPlaying(false));
    else player.pause();
  }

  return (
    <section
      id={sectionName}
      className="dark-green-colorway bg-[linear-gradient(180deg,var(--color-soft-black)_28%,var(--color-kale)_95%)] bg-no-repeat">
      <div className="sticky top-0 z-[1] flex flex-col items-center justify-center px-6 pt-32 text-center sm:pt-[134px]">
        {eyebrowText && <RichTextHeadline headline={eyebrowText} level="p" />}
        <div {...liveUpdateAttrs(sys.id, 'headline')}>
          <DisplayHeadline
            headline={headline}
            level={headingLevel}
            lineClassName="animate-hero-line-rise motion-reduce:animate-none"
            accentClassName="animate-hero-fade-in-accent motion-reduce:animate-none"
            className="font-sans text-[clamp(3.1rem,5.42vw,4.875rem)] font-light leading-[1.08] tracking-[-0.005em] [&>span]:block"
          />
        </div>
        {copy && (
          <div className="mt-6 max-w-3xl" {...liveUpdateAttrs(sys.id, 'copy')}>
            <RichTextCopy copy={copy} />
          </div>
        )}
        {isValidCta(props.cta) ? (
          <Link
            sys={props.cta.sys}
            href={props.cta.url}
            internalPage={props.cta.internalPage}
            target={props.cta.target}
            withCtaAnalytics
            className={`${CTA_ANIMATION} mt-8 inline-flex h-[42px] items-center rounded-md border border-content-inverse px-6 text-base font-medium leading-5 text-content-inverse transition-colors hover:border-fern hover:bg-fern hover:text-reserve focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-content-inverse`}>
            {props.cta.text}
          </Link>
        ) : (
          <HeroCTA {...props} className={`${CTA_ANIMATION} mt-8`} />
        )}
        {/* HeroCTA renders legal disclaimers itself; the styled button above would otherwise drop them. */}
        {isValidCta(props.cta) && props.disclaimers && (
          <div
            className="hero-disclaimers mt-4 max-w-3xl font-sans text-legal"
            {...liveUpdateAttrs(sys.id, 'disclaimers')}>
            <RichTextCopy copy={props.disclaimers} ySpacing="none" />
          </div>
        )}
      </div>
      {(desktopMedia || mobileMedia || videoUrl) && (
        <div className="relative mt-24 h-[100svh] min-h-[540px] sm:mt-[134px]">
          <div
            className="absolute inset-x-0 bottom-0 top-[25.625vh] bg-[linear-gradient(180deg,transparent,var(--color-kale))]"
            aria-hidden="true"
          />
          <div
            ref={videoShellRef}
            className={`origin-center scale-[var(--video-scale,0.94)] transition-transform duration-[160ms] ease-linear motion-reduce:scale-100 motion-reduce:transition-none relative z-[2] h-full w-full overflow-hidden rounded-t-[10px]`}>
            <div className="absolute inset-0 animate-hero-video-enter motion-reduce:animate-none">
              <Media
                media={desktopMedia}
                mobileMedia={mobileMedia}
                roundedImage={false}
                className="h-full w-full object-cover"
                sizes="100vw"
              />
              {videoUrl && (
                <>
                  <video
                    ref={videoRef}
                    className="absolute inset-0 h-full w-full object-cover"
                    poster={video?.previewImage?.url}
                    muted
                    loop
                    playsInline
                    aria-hidden="true"
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}>
                    <source src={videoUrl} type={video?.video?.contentType} />
                  </video>
                  <button
                    type="button"
                    onClick={handleVideoToggle}
                    aria-label={isPlaying ? 'Pause video' : 'Play video'}
                    className="absolute bottom-8 right-8 h-[62px] w-[62px] rounded-full text-content-inverse focus-visible:outline-2 focus-visible:outline-offset-4">
                    <svg aria-hidden="true" width="62" height="62" viewBox="0 0 62 62" fill="none">
                      <circle
                        cx="31"
                        cy="31"
                        r="28.886"
                        stroke="currentColor"
                        strokeOpacity="0.6"
                        strokeWidth="4.227"
                      />
                      {isPlaying ? (
                        <g fill="currentColor" fillOpacity="0.6">
                          <rect x="23.258" y="16.909" width="4.227" height="28.182" rx="1" />
                          <rect x="34.531" y="16.909" width="4.227" height="28.182" rx="1" />
                        </g>
                      ) : (
                        <path
                          d="M22.542 19.8555C22.542 18.5727 23.9532 17.7909 25.041 18.4707L42.8721 29.6143C43.8955 30.254 43.8955 31.745 42.8721 32.3848L25.041 43.5283C23.9532 44.2082 22.542 43.4264 22.542 42.1436V19.8555Z"
                          fill="currentColor"
                          fillOpacity="0.6"
                        />
                      )}
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
